import { isBlank } from "#/shared/lib/type-checks";
import { toValue } from "vue";
import { trimToUndefined } from "#ce/utils/props";

/**
 * Returns helpers for prompting for a location when distance sorting has none.
 * - `shouldPromptFor(value)`: true for `distance` with a near-filter selector and no location in its form or completed params (`near`, `zip`, or `lat` + `lng`). Uses the page URL only when params is undefined.
 * - `promptForLocation()`: opens the near filter and scrolls it into view when found.
 * - `findNearFilter()`: returns the matching near filter under root when found.
 * - `isLocationResolutionEvent(event, currentSortValue)`: checks whether a change supplies a location while distance sorting is active.
 * @param {import("vue").MaybeRefOrGetter<string>} nearFilterSelectorRef
 * @param {object} [options]
 * @param {import("vue").MaybeRefOrGetter<Object|undefined>} [options.params] completed finder criteria
 * @param {import("vue").MaybeRefOrGetter<Document|ShadowRoot|Element>} [options.root=document] near-filter lookup root
 */
export function useDistanceSortPrompt(nearFilterSelectorRef, { params, root = document } = {}) {
  function findNearFilter() {
    const selector = toValue(nearFilterSelectorRef);

    return selector ? toValue(root)?.querySelector(selector) : null;
  }

  function hasLocationValue(near, lat, lng) {
    return Boolean(trimToUndefined(near)) || (!isBlank(trimToUndefined(lat)) && !isBlank(trimToUndefined(lng)));
  }

  function hasLocationInParams(params) {
    return hasLocationValue(params.get("near"), params.get("lat"), params.get("lng"));
  }

  function hasLocationInSearch() {
    const completed = toValue(params);
    return completed === undefined
      ? hasLocationInParams(new URLSearchParams(window.location.search))
      : hasLocationValue(trimToUndefined(completed.near) || completed.zip, completed.lat, completed.lng);
  }

  function hasLocationInNearFilterValue() {
    const filterForm = findNearFilter()?.closest("form");
    if (!filterForm) {
      return false;
    }

    return hasLocationInParams(new FormData(filterForm));
  }

  function hasLocationForDistanceSort() {
    return hasLocationInNearFilterValue() || hasLocationInSearch();
  }

  function shouldPromptFor(value) {
    return value === "distance" && Boolean(toValue(nearFilterSelectorRef)) && !hasLocationForDistanceSort();
  }

  function promptForLocation() {
    const nearFilter = findNearFilter();
    if (!nearFilter) {
      return;
    }

    nearFilter.open = false;
    requestAnimationFrame(() => {
      nearFilter.open = true;
      nearFilter.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
    });
  }

  function isLocationResolutionEvent(event, currentSortValue) {
    if (currentSortValue !== "distance") {
      return false;
    }

    const nearFilter = findNearFilter();
    if (!nearFilter) {
      return false;
    }

    const changedInsideNearFilter = event.composed
      ? event.composedPath().includes(nearFilter)
      : nearFilter.contains(event.target);

    return changedInsideNearFilter && hasLocationInNearFilterValue();
  }

  return { shouldPromptFor, promptForLocation, findNearFilter, isLocationResolutionEvent };
}
